import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookingsSetUpPayload,
  ConfirmBookingPayload,
  GetBookingsParams,
  GetBookingsResponse,
  GetMyBookingsParams,
  GetMyBookingsResponse,
  GetSpeakingInvitationsParams,
  SpeakingInvitationsResponse,
  InviteToSpeakPayload,
  SendCorrespondence,
  RespondToInvitation,
} from "interfaces";
import {
  getBookingCalendarsService,
  getMyBookingsService,
  createBookingCalendarService,
  confirmBookingService,
  cancelBookingSlotService,
  sendMeetingReminderService,
  inviteToSpeakService,
  getSpeakingInvitationService,
  getSpeakingCorrespondenceService,
  sendCorrespondenceService,
  respondToInvitationService,
} from "services";

// Query Hooks (GET requests)
export const useGetBookingCalendars = (
  params: GetBookingsParams = {},
  enabled = true,
) => {
  return useQuery<GetBookingsResponse>({
    queryKey: ["bookingCalendars", params],
    queryFn: () => getBookingCalendarsService(params),
    enabled,
  });
};

export const useGetMyBookings = (params: GetMyBookingsParams = {}) => {
  return useQuery<GetMyBookingsResponse>({
    queryKey: ["myBookings", params],
    queryFn: () => getMyBookingsService(params),
  });
};

// Mutation Hooks (POST requests)
export const useCreateBookingCalendar = () => {
  return useMutation<string, Error, BookingsSetUpPayload>({
    mutationFn: (bookingData: BookingsSetUpPayload) =>
      createBookingCalendarService(bookingData),
  });
};

export const useConfirmBooking = () => {
  return useMutation<
    void,
    Error,
    { bookingId: string } & ConfirmBookingPayload
  >({
    mutationFn: ({ bookingId, ...payload }) =>
      confirmBookingService(bookingId, payload),
  });
};

export const useCancelBookingSlot = () => {
  return useMutation<void, Error, string>({
    mutationFn: (slotId: string) => cancelBookingSlotService(slotId),
  });
};

export const useSendBookingSlotReminder = () => {
  return useMutation<void, Error, string>({
    mutationFn: (slotId: string) => sendMeetingReminderService(slotId),
  });
};

export const useInviteToSpeak = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { invitationId: string },
    Error,
    { speakerId: string } & InviteToSpeakPayload
  >(
    {
      mutationFn: ({ speakerId, ...payload }) =>
        inviteToSpeakService(speakerId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["speaking-invitations"] });
      },
    },
  );
};

export const useGetInvitationToSpeak = (
  params: GetSpeakingInvitationsParams = {},
) => {
  return useQuery<SpeakingInvitationsResponse>({
    queryKey: ["speaking-invitations", params],
    queryFn: () => getSpeakingInvitationService(params),
  });
};

export const useGetSpeakingCorrespondence = (id: string) => {
  return useQuery({
    queryKey: ["speaking-correspondence", id],
    queryFn: () => getSpeakingCorrespondenceService(id),
    enabled: !!id,
  });
};

export const useSendCorrespondence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendCorrespondence) =>
      sendCorrespondenceService(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["speaking-correspondence", variables.id],
      });
    },
  });
};

export const useRespondToInvitation = () => {
  return useMutation({
    mutationFn: (payload: RespondToInvitation) => {
      return respondToInvitationService(payload);
    },
  });
};
